import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutationData } from "../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { NOTICE_STATUS } from "../../utils/common";
import {
  NOTICE_CUSTOM_VALUE,
  NOTICE_CUSTOM_URL_OPTION,
  NOTICE_CUSTOM_COPYRIGHT_OPTION,
  NOTICE_CUSTOM_TEXT_OPTION,
  CHARACTER_LENGTH,
} from "../../constants";
import CustomTextarea from "../Forms/CustomTextarea";
import { NoticeInformationTable } from "../Tables";
import NoticeInformationEditModal from "./NoticeInformationEditModal";
import ConfirmActionModal from "./ConfirmActionModal";
import { useClientConfig } from "@contexts/ClientConfigContext";
import useEscapeKey from "@hooks/useEscapeKey";

const NoticeInformationListModal = ({
  isOpen,
  onClose,
  onComplete,
  noticeInfo,
  noticeInfoComponents = [],
  scanComponents = [],
  updateNoticeInfoNoticeInfoWriteStatus,
  isPending = false,
  "data-testid": dataTestId = "notice-information-list-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // State management
  const [noticeHeader, setNoticeHeader] = useState("");
  const [componentData, setComponentData] = useState([]);
  const [currentEditingIndex, setCurrentEditingIndex] = useState(-1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [deletedItems, setDeletedItems] = useState([]);
  const [showWriteCompletionConfirm, setShowWriteCompletionConfirm] = useState(false);
  const [showIncompleteRowWarning, setShowIncompleteRowWarning] = useState(false);
  const clientConfigContext = useClientConfig();

  // Safe destructuring with null checks
  const clientConfig = clientConfigContext?.clientConfig || {};
  const clientConfigLoading = clientConfigContext?.isLoading || false;

  // API mutation for checking existing notice info components
  const checkExistingNoticeInfoComponents = useMutationData(
    () => `/notice_info_component_by_notice_info_seq/${noticeInfo?.noticeInfoSeq}`,
    { method: "GET" },
    {
      onSuccess: (data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          // Process all components while maintaining original order
          const processedComponents = data.map((component) => ({
            ...component,
            componentName: component.componentName || component.component,
            licenseName: component.licenseName || component.licenseIdentifier,
            noticeInfoComponentSeq: component.noticeInfoComponentSeq,
            // Map API field names to expected field names
            url: component.url || "",
            copyright: component.copyrightInfo || component.copyright || "",
            licenseText: component.licenseText || "",
            isUpdated: component.isUpdated
          }));

          // Process components while maintaining their original order
          processComponentsInOrder(processedComponents);
        } else if (Array.isArray(scanComponents) && scanComponents.length > 0) {
          // No data exists, need to initialize with component licenses
          initializeWithComponentLicenses();
        } else {
          // Nothing to initialize, just clear loading state
          setComponentData([]);
          setIsTableLoading(false);
          setIsInitializing(false);
        }
      },
      onError: (error) => {
        console.error("Error checking existing notice info components:", error);
      },
    },
  );

  // API mutation for fetching license data
  const fetchLicenseData = useMutationData(
    () => "/component_licenses",
    { method: "POST" },
    {
      onSuccess: (data) => {
        if (data && Array.isArray(data)) {
          // Process license data to create options for each component
          const processedComponents = data.map((component) => {
            if (component && component.componentLicenses) {
              const options = processLicenseOptions(component.componentLicenses);
              return {
                ...component,
                ...options,
              };
            }

            return {
              ...component,
              urlOptions: [NOTICE_CUSTOM_URL_OPTION],
              copyrightOptions: [NOTICE_CUSTOM_COPYRIGHT_OPTION],
              licenseTextOptions: [NOTICE_CUSTOM_TEXT_OPTION],
            };
          });
          setComponentData(processedComponents);
        }
        setIsTableLoading(false);
        setIsInitializing(false);
      },
      onError: (error) => {
        console.error("Error fetching license data:", error);
        setIsTableLoading(false);
        setIsInitializing(false);
      },
    },
  );

  // API mutation for initializing notice info with component licenses
  const initializeNoticeInfoWithLicenses = useMutationData(
    () => "/notice_info_notice_info_write_status",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        // After successful initialization, fetch the created data from notice_info_component_by_notice_info_seq
        checkExistingNoticeInfoComponents.mutate();
      },
      onError: (error) => {
        console.error("Error initializing notice info with licenses:", error);
        setIsTableLoading(false);
        setIsInitializing(false);
      },
    },
  );

  // Helper function to process components while maintaining their original order
  const processComponentsInOrder = (allComponents) => {

  // Helper function to handle mixed components (updated and non-updated)
    try {
      if (!Array.isArray(scanComponents) || scanComponents.length === 0) {
        setIsTableLoading(false);
        setIsInitializing(false);
        return;
      }
      // All components are already in their original order from the API
      
      // Fetch license data for all components
      const requestBody = allComponents.map(comp => ({
        componentName: comp.componentName || comp.component,
        componentVersion: comp.componentVersion || "",
        licenseIdentifier: comp.licenseName || comp.licenseIdentifier || "",
      }));

      fetchLicenseData.mutate(requestBody, {
        onSuccess: (licenseData) => {
          // Process all components while maintaining order
          const processedComponents = allComponents.map((comp) => {
            const licenseDataForComponent = licenseData.find(
              (licenseComp) =>
                licenseComp.componentName === (comp.componentName || comp.component) &&
                licenseComp.componentVersion === (comp.componentVersion || ""),
            );

            if (licenseDataForComponent && licenseDataForComponent.componentLicenses) {
              // For updated components (isUpdated === "Y"), use special processing
              if (comp.isUpdated === "Y") {
                const options = processLicenseOptions(licenseDataForComponent.componentLicenses, comp);
                return {
                  ...comp,
                  ...options,
                  // Override with the actual values from the component
                  url: comp.url || "",
                  copyright: comp.copyright || "",
                  licenseText: comp.licenseText || "",
                  // Set selections based on actual values
                  urlSelection: comp.url ? NOTICE_CUSTOM_URL_OPTION : null,
                  copyrightSelection: comp.copyright ? NOTICE_CUSTOM_COPYRIGHT_OPTION : null,
                  licenseTextSelection: comp.licenseText ? NOTICE_CUSTOM_TEXT_OPTION : null,
                };
              } else {
                // For non-updated components, use standard processing
                const options = processLicenseOptions(licenseDataForComponent.componentLicenses, comp);
                return {
                  ...comp,
                  ...options,
                };
              }
            }

            // Fallback to ETC options if no license data found
            const urlSelection = findMatchingSelection(
              comp.url, 
              [NOTICE_CUSTOM_URL_OPTION], 
              comp.urlSelection
            );
            const copyrightSelection = findMatchingSelection(
              comp.copyright, 
              [NOTICE_CUSTOM_COPYRIGHT_OPTION], 
              comp.copyrightSelection
            );
            const licenseTextSelection = findMatchingSelection(
              comp.licenseText, 
              [NOTICE_CUSTOM_TEXT_OPTION], 
              comp.licenseTextSelection
            );

            return {
              ...comp,
              urlOptions: [NOTICE_CUSTOM_URL_OPTION],
              copyrightOptions: [NOTICE_CUSTOM_COPYRIGHT_OPTION],
              licenseTextOptions: [NOTICE_CUSTOM_TEXT_OPTION],
              urlSelection,
              copyrightSelection,
              licenseTextSelection,
            };
          });

          setComponentData(processedComponents);
          setIsTableLoading(false);
          setIsInitializing(false);
        },
        onError: (error) => {
          console.error("Error fetching license data:", error);
          // Fallback: use existing data with ETC options while maintaining order
          const fallbackComponents = allComponents.map(comp => {
            const urlSelection = findMatchingSelection(
              comp.url, 
              [NOTICE_CUSTOM_URL_OPTION], 
              comp.urlSelection
            );
            const copyrightSelection = findMatchingSelection(
              comp.copyright, 
              [NOTICE_CUSTOM_COPYRIGHT_OPTION], 
              comp.copyrightSelection
            );
            const licenseTextSelection = findMatchingSelection(
              comp.licenseText, 
              [NOTICE_CUSTOM_TEXT_OPTION], 
              comp.licenseTextSelection
            );

            return {
              ...comp,
              urlOptions: [NOTICE_CUSTOM_URL_OPTION],
              copyrightOptions: [NOTICE_CUSTOM_COPYRIGHT_OPTION],
              licenseTextOptions: [NOTICE_CUSTOM_TEXT_OPTION],
              urlSelection,
              copyrightSelection,
              licenseTextSelection,
            };
          });

          setComponentData(fallbackComponents);
          setIsTableLoading(false);
          setIsInitializing(false);
        }
      });
    } catch (error) {
      console.error("Error in processComponentsInOrder:", error);
      setIsTableLoading(false);
      setIsInitializing(false);
    }
  };

  // Helper function to initialize with component licenses
  const initializeWithComponentLicenses = () => {
    try {
      // setIsTableLoading(true);
      // setIsInitializing(true);

      // First, get component licenses data
      const requestBody = scanComponents.map((component) => ({
        componentName: component.componentName || component.component,
        componentVersion: component.componentVersion || "",
        licenseIdentifier: component.licenseName || "",
      }));

      fetchLicenseData.mutate(requestBody, {
        onSuccess: (licenseData) => {
          if (licenseData && Array.isArray(licenseData)) {
            // Process the license data to create component licenses for initialization
            const componentLicenses = licenseData.map((comp) => {
              const baseObj = {
                componentName: comp.componentName || "",
                componentVersion: comp.componentVersion || "",
                licenseIdentifier: comp.licenseIdentifier || "",
                url: "",
                copyrightInfo: "",
                licenseText: "",
              };

              if (comp.componentLicenses && comp.componentLicenses.length > 0) {
                let license = comp.componentLicenses.find((l) => l.originalFlag === "Y");
                if (!license) {
                  license = comp.componentLicenses[0];
                }

                if (license.url) baseObj.url = license.url;
                if (license.copyright) baseObj.copyrightInfo = license.copyright;
                if (license.licenseText) baseObj.licenseText = license.licenseText;
              }

              return baseObj;
            });

            // Initialize notice info with component licenses
            const payload = {
              noticeInfoSeq: noticeInfo?.noticeInfoSeq,
              noticeInfoWriteStatusCd: NOTICE_STATUS.REQUEST,
              noticeInfoHeader: noticeInfo?.noticeInfoHeader || "",
              noticeInfoComponents: componentLicenses.map((item) => ({
                componentName: item.componentName || item.component || "",
                componentVersion: item.componentVersion || "",
                db: false,
                delete: false,
                licenseName: item.licenseIdentifier || "",
                url: item.url || "",
                copyrightInfo: item.copyrightInfo || "",
                licenseText: item.licenseText || "",
              })),
              componentLicenses: [],
            };
            initializeNoticeInfoWithLicenses.mutate(payload);
          } else {
            setIsTableLoading(false);
            setIsInitializing(false);
          }
        },
        onError: (error) => {
          console.error("Error fetching license data for initialization:", error);
          setIsTableLoading(false);
          setIsInitializing(false);
        },
      });
    } catch (error) {
      console.error("Error initializing with component licenses:", error);
      setIsTableLoading(false);
      setIsInitializing(false);
    }
  };

  // Helper function to find matching selection based on existing data
  const findMatchingSelection = (existingValue, options, existingSelection) => {
    // If we already have a selection and it matches the existing value, keep it
    if (existingSelection && existingSelection.name === existingValue) {
      return existingSelection;
    }
    
    // Try to find a matching option by name/value
    const matchingOption = options.find(option => option.name === existingValue);
    if (matchingOption) {
      return matchingOption;
    }
    
    // If no exact match found and we have a value, it's likely a custom value (Custom)
    if (existingValue && existingValue.trim() !== "") {
      return { name: NOTICE_CUSTOM_VALUE, original: NOTICE_CUSTOM_VALUE, displayName: NOTICE_CUSTOM_VALUE };
    }
    
    // Return null if no value
    return null;
  };

  // Helper function to process license options
  const processLicenseOptions = (componentLicenses, existingData = null) => {
    const uniqueUrls = new Set();
    const uniqueCopyrights = new Set();
    const uniqueTexts = new Set();

    const urlWithOriginalFlag = new Map();
    const copyrightWithOriginalFlag = new Map();
    const textWithOriginalFlag = new Map();

    componentLicenses.forEach((license) => {
      if (license.url) {
        uniqueUrls.add(license.url);
        if (license.originalFlag === "Y") {
          urlWithOriginalFlag.set(license.url, true);
        }
      }
      if (license.copyright) {
        uniqueCopyrights.add(license.copyright);
        if (license.originalFlag === "Y") {
          copyrightWithOriginalFlag.set(license.copyright, true);
        }
      }
      if (license.licenseText) {
        uniqueTexts.add(license.licenseText);
        if (license.originalFlag === "Y") {
          textWithOriginalFlag.set(license.licenseText, true);
        }
      }
    });

    const urlOpts = Array.from(uniqueUrls).map((url) => ({
      name: url,
      original: urlWithOriginalFlag.get(url) ? "Y" : "N",
      displayName: url.length > 90 ? url.substring(0, 90) + "..." : url,
      originalFlag: urlWithOriginalFlag.get(url) ? "Y" : "N",
    }));

    const copyrightOpts = Array.from(uniqueCopyrights).map((copyright) => ({
      name: copyright,
      original: copyrightWithOriginalFlag.get(copyright) ? "Y" : "N",
      displayName: copyright.length > 90 ? copyright.substring(0, 90) + "..." : copyright,
      originalFlag: copyrightWithOriginalFlag.get(copyright) ? "Y" : "N",
    }));

    const textOpts = Array.from(uniqueTexts).map((text) => ({
      name: text,
      original: textWithOriginalFlag.get(text) ? "Y" : "N",
      displayName: text.length > 90 ? text.substring(0, 90) + "..." : text,
      originalFlag: textWithOriginalFlag.get(text) ? "Y" : "N",
    }));

    const finalUrlOpts = [...urlOpts, NOTICE_CUSTOM_URL_OPTION];
    const finalCopyrightOpts = [...copyrightOpts, NOTICE_CUSTOM_COPYRIGHT_OPTION];
    const finalTextOpts = [...textOpts, NOTICE_CUSTOM_TEXT_OPTION];

    // Check if there are any items with originalFlag === "Y"
    const hasOriginalUrl = urlOpts.some((opt) => opt.originalFlag === "Y");
    const hasOriginalCopyright = copyrightOpts.some((opt) => opt.originalFlag === "Y");
    const hasOriginalText = textOpts.some((opt) => opt.originalFlag === "Y");

    // If we have existing data (second click), apply the new logic
    if (existingData) {
      // Find original options
      const originalUrlOption = urlOpts.find((opt) => opt.originalFlag === "Y");
      const originalCopyrightOption = copyrightOpts.find((opt) => opt.originalFlag === "Y");
      const originalTextOption = textOpts.find((opt) => opt.originalFlag === "Y");

      // If there are original options, use them; otherwise set to empty/null
      const urlSelection = hasOriginalUrl ? originalUrlOption : null;
      const copyrightSelection = hasOriginalCopyright ? originalCopyrightOption : null;
      const licenseTextSelection = hasOriginalText ? originalTextOption : null;

      return {
        urlOptions: finalUrlOpts,
        copyrightOptions: finalCopyrightOpts,
        licenseTextOptions: finalTextOpts,
        urlSelection,
        copyrightSelection,
        licenseTextSelection,
        url: hasOriginalUrl ? (originalUrlOption ? originalUrlOption.name : "") : "",
        copyright: hasOriginalCopyright ? (originalCopyrightOption ? originalCopyrightOption.name : "") : "",
        licenseText: hasOriginalText ? (originalTextOption ? originalTextOption.name : "") : "",
      };
    }

    // Default behavior for new components (first click)
    const originalUrlOption = urlOpts.find((opt) => opt.originalFlag === "Y");
    const originalCopyrightOption = copyrightOpts.find((opt) => opt.originalFlag === "Y");
    const originalTextOption = textOpts.find((opt) => opt.originalFlag === "Y");

    return {
      urlOptions: finalUrlOpts,
      copyrightOptions: finalCopyrightOpts,
      licenseTextOptions: finalTextOpts,
      urlSelection: originalUrlOption || null,
      copyrightSelection: originalCopyrightOption || null,
      licenseTextSelection: originalTextOption || null,
      url: originalUrlOption ? originalUrlOption.name : "",
      copyright: originalCopyrightOption ? originalCopyrightOption.name : "",
      licenseText: originalTextOption ? originalTextOption.name : "",
    };
  };

  // Initialize component data when modal opens
  useEffect(() => {
  if (isOpen) {
    let headerToSet = "";

    if (clientConfigContext && !clientConfigLoading && clientConfig && Object.keys(clientConfig).length > 0) {
      try {
        const prefHeader = clientConfigContext.getClientConfigRemark("defaultNoticeHeader");
        if (prefHeader) headerToSet = prefHeader;
      } catch (error) {
        console.error("Error getting preference value:", error);
      }
    }

    if (noticeInfo?.noticeInfoHeader) {
      headerToSet = noticeInfo.noticeInfoHeader;
    }

    if (headerToSet) setNoticeHeader(headerToSet);

    // Check if we have existing notice info components
    if (noticeInfo?.noticeInfoSeq) {
      setIsTableLoading(true);
      setIsInitializing(true);
      checkExistingNoticeInfoComponents.mutate();
    } else {
      setIsTableLoading(false);
      setIsInitializing(false);
    }
  } else {
    // Reset loading state and discarded deletes when modal closes (Cancel = discard changes)
    setIsTableLoading(false);
    setIsInitializing(false);
    setDeletedItems([]);
  }
}, [isOpen, noticeInfo?.noticeInfoSeq, clientConfigContext, clientConfig, clientConfigLoading]);

  // Handle component item click from table
  const handleRowClick = (row, index) => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => openEditModal(index));
    } else {
      openEditModal(index);
    }
  };

  // Handle component delete from table
  const handleDelete = (row, index) => {
    const updatedData = componentData.filter((item) => item.noticeInfoComponentSeq !== row.noticeInfoComponentSeq);
    setComponentData(updatedData);
    setDeletedItems((prev) => [...prev, { ...row, isDelete: true }]);
  };

  const openEditModal = (index) => {
    setCurrentEditingIndex(index);
    setShowEditModal(true);
    setHasUnsavedChanges(false);
  };

  // Handle edit modal save
  const handleEditModalSave = async (updatedComponent) => {
    try {
      const updatedData = [...componentData];
      updatedData[currentEditingIndex] = {
        ...updatedData[currentEditingIndex],
        ...updatedComponent,
        isEdited: true,
        hasChanges: true,
      };
      setComponentData(updatedData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving component:", error);
      throw error; // Re-throw to be caught by the modal
    }
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setHasUnsavedChanges(false);
  };

  // Handle navigation with unsaved changes check
  const handleNavigation = (direction) => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => navigateToComponent(direction));
    } else {
      navigateToComponent(direction);
    }
  };

  const navigateToComponent = (direction) => {
    let newIndex;

    if (typeof direction === "number") {
      // Direct index navigation
      newIndex = Math.max(0, Math.min(direction, componentData.length - 1));
    } else {
      // Direction-based navigation
      newIndex =
        direction === "next"
          ? Math.min(currentEditingIndex + 1, componentData.length - 1)
          : Math.max(currentEditingIndex - 1, 0);
    }

    setCurrentEditingIndex(newIndex);
    setHasUnsavedChanges(false);
  };

  // Check if at least one row has URL, Copyright, and License Text all empty
  const hasIncompleteRow = () => {
    if (!Array.isArray(componentData) || componentData.length === 0) return false;
    return componentData.some((item) => {
      const url = (item.url || "").trim();
      const copyright = (item.copyright || "").trim();
      const licenseText = (item.licenseText || "").trim();
      return !url && !copyright && !licenseText;
    });
  };

  // Handle Write Completion button click: validate then show confirmation or warning
  const handleWriteCompletionClick = () => {
    if (hasIncompleteRow()) {
      setShowIncompleteRowWarning(true);
      return;
    }
    setShowWriteCompletionConfirm(true);
  };

  // Perform the actual write completion (after user confirms)
  const doWriteCompletion = async () => {
    setShowWriteCompletionConfirm(false);
    setShowIncompleteRowWarning(false);
    try {
      const transformedData = componentData.map((item) => ({
        noticeInfoComponentSeq: item.noticeInfoComponentSeq || null,
        noticeInfoSeq: noticeInfo?.noticeInfoSeq || null,
        componentName: item.componentName || "",
        componentVersion: item.componentVersion || "",
        db: true,
        delete: false,
        licenseName: item.licenseName || "",
        url: item.url || "",
        copyrightInfo: item.copyright || "",
        licenseText: item.licenseText || "",
      }));

      // Deleted items - include noticeInfoSeq so backend can identify records to delete
      const deletedData = deletedItems.map((item) => ({
        noticeInfoComponentSeq: item.noticeInfoComponentSeq || null,
        noticeInfoSeq: noticeInfo?.noticeInfoSeq || null,
        componentName: item.componentName || "",
        componentVersion: item.componentVersion || "",
        db: true,
        delete: true,
        licenseName: item.licenseName || "",
        url: item.url || "",
        copyrightInfo: item.copyright || "",
        licenseText: item.licenseText || "",
      }));

      const transformedComponentLicenses = componentData
        .map((item) => {
          const hasCustomSelection =
            (item.urlSelection && item.urlSelection.name === NOTICE_CUSTOM_VALUE && item.url) ||
            (item.copyrightSelection && item.copyrightSelection.name === NOTICE_CUSTOM_VALUE && item.copyright) ||
            (item.licenseTextSelection && item.licenseTextSelection.name === NOTICE_CUSTOM_VALUE && item.licenseText);

          if (!hasCustomSelection) {
            return null;
          }

          const baseObj = {
            componentName: item.componentName || "",
            componentVersion: item.componentVersion || "",
            licenseIdentifier: item.licenseName || "",
          };

          if (item.urlSelection && item.urlSelection.name === NOTICE_CUSTOM_VALUE && item.url) {
            baseObj.url = item.url;
          }

          if (item.copyrightSelection && item.copyrightSelection.name === NOTICE_CUSTOM_VALUE && item.copyright) {
            baseObj.copyright = item.copyright;
          }

          if (item.licenseTextSelection && item.licenseTextSelection.name === NOTICE_CUSTOM_VALUE && item.licenseText) {
            baseObj.licenseText = item.licenseText;
          }

          return baseObj;
        })
        .filter(Boolean);

      const payload = {
        noticeInfoSeq: noticeInfo?.noticeInfoSeq,
        noticeInfoWriteStatusCd: NOTICE_STATUS.DONE,
        noticeInfoHeader: noticeHeader,
        noticeInfoComponents: [...transformedData, ...deletedData],
        componentLicenses: transformedComponentLicenses || [],
      };
      updateNoticeInfoNoticeInfoWriteStatus.mutate(payload, {
        onSuccess: () => {
          setDeletedItems([]); // Clear so next open gets fresh state from API
          if (onComplete) {
            onComplete();
          }
          onClose();
        },
      });
    } catch (error) {
      console.error("Error updating notice info:", error);
    }
  };

  const handleWriteCompletionConfirm = () => {
    doWriteCompletion();
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <>
      <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
        <div
          className={`${colors.bgModal} max-h-[80vh] w-[1000px] min-w-[1000px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
          data-testid={dataTestId}
        >
          {/* Header */}
          <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
            <div className="flex items-center justify-between">
              <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                {t("project.notice-infor.notice-infor-list")}
              </h2>
            </div>
          </div>

          {/* Notice Header Section */}
          <div className="px-6 pb-4 flex-shrink-0" data-testid={fieldContainerTestId("notice-header")}>
            <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("notice-header")}>
              {t("project.notice-infor.notice-header")}
            </label>
            <CustomTextarea
              value={noticeHeader || ""}
              onChange={(e) => setNoticeHeader(e.target.value)}
              rows={4}
              maxLength={CHARACTER_LENGTH.LICENSE_HEADER}
              className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
              data-testid={fieldInputTestId("notice-header")}
            />
            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={`${dataTestId}-field-notice-header-char-count`}>
              {(noticeHeader || "").length}/{CHARACTER_LENGTH.LICENSE_HEADER}
            </p>
          </div>

          {/* Table Section - Scrollable */}
          <div className="flex flex-1 flex-col px-6 min-h-0" data-testid={`${dataTestId}-content`}>
            <NoticeInformationTable
              data={componentData}
              onRowClick={handleRowClick}
              onDelete={handleDelete}
              onDataChange={setComponentData}
              loading={isTableLoading || isInitializing}
              showPagination={true}
              scrollableBody={true}
              seq="noticeInfoComponentSeq"
            />
          </div>

          {/* Footer - Sticky */}
          <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
            <div className="flex items-center justify-end">
              <div className="flex items-center space-x-3">
                <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                  {t("common.cancel-button")}
                </OutlineButton>
                <PrimaryButton
                  data-testid={`${dataTestId}-confirm-button`}
                  disabled={isPending || isInitializing}
                  onClick={handleWriteCompletionClick}
                >
                  {isPending || isInitializing ? (
                    <div className="flex items-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      {isInitializing ? t("dashboard.initializing") : t("common.processing")}
                    </div>
                  ) : (
                    t("project.notice-infor.write-completion")
                  )}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <NoticeInformationEditModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          onSave={handleEditModalSave}
          onNavigate={handleNavigation}
          onUnsavedChanges={setHasUnsavedChanges}
          component={componentData[currentEditingIndex]}
          currentIndex={currentEditingIndex}
          totalCount={componentData.length}
          componentData={componentData}
        />
      )}

      {/* Write Completion Confirmation Dialog */}
      <ConfirmActionModal
        isOpen={showWriteCompletionConfirm}
        onClose={() => setShowWriteCompletionConfirm(false)}
        onConfirm={handleWriteCompletionConfirm}
        title={t("project.notice-infor.write-completion-confirm-title")}
        description={t("project.notice-infor.write-completion-confirm-message")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel-button")}
        loading={isPending}
      />

      {/* Incomplete Row Warning (URL, Copyright, License Text all empty in at least one row) */}
      <ConfirmActionModal
        isOpen={showIncompleteRowWarning}
        onClose={() => setShowIncompleteRowWarning(false)}
        onConfirm={handleWriteCompletionConfirm}
        title={t("project.notice-infor.incomplete-row-warning-title")}
        description={t("project.notice-infor.incomplete-row-warning-message")}
        confirmText={t("common.ok-button")}
        cancelText={t("common.cancel-button")}
        confirmButtonVariant="primary"
      />
    </>
  );
};

export default NoticeInformationListModal;
